import { useState, useEffect } from 'react';
import { HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';
import { useConfig } from 'config';
import useFetch from 'hooks/useFetch';
import { useAuth } from 'auth';

const useSignalR = (
  setMessage: React.Dispatch<any>,
  hubName: string,
  groupName?: string,
  enabled = true
) => {
  const [connection, setConnection] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionId, setConnectionId] = useState('');
  const { signalRUrl, signalRSubscriptionKey, xFunctionsKey } = useConfig();
  const { tokenInfo } = useAuth();

  const { data } = useFetch(enabled ? `${signalRUrl}/negotiate/${hubName}` : '', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': signalRSubscriptionKey,
      'X-functions-Key': xFunctionsKey,
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    },
  });

  useEffect(() => {
    if (data) {
      const newConnection = new HubConnectionBuilder()
        .withUrl(data.url, {
          accessTokenFactory: () => data.accessToken,
          transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .build();

      setConnection(newConnection);
    }
  }, [data]);

  useEffect(() => {
    if (connection) {
      const joinGroup = async (connectionId) => {
        if (groupName && !connectionId) {
          console.log(`Cannot join SignalR group ${groupName} because connectionId is empty.`);
          return;
        }

        if (groupName && connectionId) {
          const response = await fetch(`${signalRUrl}/joinGroup/${hubName}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Ocp-Apim-Subscription-Key': signalRSubscriptionKey,
              'X-functions-Key': xFunctionsKey,
              Authorization: `Bearer ${tokenInfo.accessToken}`,
            },
            body: JSON.stringify({
              connectionId,
              groupName,
            }),
          });

          if (!response.ok) {
            throw new Error(`Failed to join SignalR group ${groupName}`);
          }
        }
      };

      connection
        .start()
        .then(async () => {
          setIsConnected(true);
          setConnectionId(connection.connectionId);
          await joinGroup(connection.connectionId);
        })
        .catch((e) => {
          console.log(e);
        });

      connection.onreconnected(async (newConnectionId) => {
        setIsConnected(true);
        setConnectionId(newConnectionId);
        try {
          await joinGroup(newConnectionId);
        } catch (e) {
          console.log(e);
        }
      });

      connection.onreconnecting(() => {
        setIsConnected(false);
      });

      connection.on('ReceiveMessage', (message) => {
        setMessage(message);
      });
    }

    return () => {
      if (connection) {
        connection.stop();
        setIsConnected(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    connection,
    groupName,
    hubName,
    signalRSubscriptionKey,
    signalRUrl,
    tokenInfo.accessToken,
    xFunctionsKey,
  ]);

  return {
    isConnected,
    connectionId,
  };
};

export default useSignalR;
